import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useState, useEffect } from "react";
import { SimpleDialogComponent } from "src/general_components/SimpleDialogComponent";
import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";
import {
  ocrPlantillasCrear,
  ocrPlantillasActualizar,
  ocrPlantillasGuardarCampos,
  ocrPlantillasGuardarRegiones,
  ocrMetadatosListar,
} from "../helpers/OcrData";
import { RegionesEditor } from "./Digitalizaciones/AltaMasivaOcr/RegionesEditor";

const TIPO_CAMPO_OPTIONS = [
  { value: "texto", label: "Texto" },
  { value: "fecha", label: "Fecha" },
  { value: "numero", label: "Número" },
  { value: "boolean", label: "Booleano" },
];

const TIPO_PLANTILLA_OPTIONS = [
  { value: "extraccion", label: "Extracción" },
  { value: "validacion", label: "Validación" },
];

const campoVacio = () => ({
  _key: Math.random().toString(36).slice(2),
  etiqueta: "",
  clave_textract: "",
  tipo_campo: "texto",
  obligatorio: false,
  orden: 0,
  id_metadatos: null,
});

export const FormOcrPlantilla = ({
  open,
  onClose,
  onSuccess,
  plantilla,
}) => {
  const esEdicion = Boolean(plantilla?.id);

  const tiposContenidosList = JSON.parse(localStorage.getItem("siam-tipo_contenidos"))?.tipo_contenidos || [];

  const [form, setForm] = useState({
    nombre: plantilla?.nombre ?? "",
    descripcion: plantilla?.descripcion ?? "",
    tipo: plantilla?.tipo ?? "extraccion",
    umbral_confianza: plantilla?.umbral_confianza ?? 80,
    id_tipo_contenidos: plantilla?.id_tipo_contenidos ?? null,
    cuentas_ids: plantilla?.cuentas_ids ?? [],
  });

  const [campos, setCampos] = useState(() => {
    if (plantilla?.campos && plantilla.campos.length > 0) {
      return plantilla.campos.map((c) => ({ ...c, _key: c.id ?? Math.random().toString(36).slice(2) }));
    }
    return [campoVacio()];
  });

  const [activeTab, setActiveTab] = useState(0);
  const [regiones, setRegiones] = useState(plantilla?.regiones ?? []);
  const [imagenReferencia, setImagenReferencia] = useState(plantilla?.imagen_referencia ?? null);

  const [metadatosOpciones, setMetadatosOpciones] = useState([]);
  const [cuentasOpciones, setCuentasOpciones] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // Cargar cuentas desde localStorage (misma fuente que el selector del dashboard)
  useEffect(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("siam-cuentas_personas_juridicas"));
      const lista = Array.isArray(raw?.cuentas_personas_juridicas)
        ? raw.cuentas_personas_juridicas
        : [];
      setCuentasOpciones(lista);
    } catch { setCuentasOpciones([]); }
  }, []);

  // Reload metadatos when tipo_contenidos changes
  useEffect(() => {
    ocrMetadatosListar(form.id_tipo_contenidos || null).then((data) => {
      setMetadatosOpciones(data);
      // Clear id_metadatos assignments that no longer exist in the new list
      if (data.length > 0) {
        const validIds = new Set(data.map((m) => String(m.id)));
        setCampos((prev) =>
          prev.map((c) =>
            c.id_metadatos && !validIds.has(String(c.id_metadatos))
              ? { ...c, id_metadatos: null }
              : c
          )
        );
      }
    });
  }, [form.id_tipo_contenidos]);

  // Returns metadatos options available for a given campo (excludes those already used by other campos)
  const metadatosDisponibles = (campoKey) => {
    const usedIds = new Set(
      campos
        .filter((c) => c._key !== campoKey && c.id_metadatos)
        .map((c) => String(c.id_metadatos))
    );
    return metadatosOpciones.filter((m) => !usedIds.has(String(m.id)));
  };

  const showSnack = (message, severity = "error") =>
    setSnack({ open: true, message, severity });

  const handleFormChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleCampoChange = (key, field) => (e) => {
    const value = field === "obligatorio" ? e.target.checked : e.target.value;
    setCampos((prev) =>
      prev.map((c) => (c._key === key ? { ...c, [field]: value } : c))
    );
  };

  const handleCampoMetadatos = (key, newValue) => {
    setCampos((prev) =>
      prev.map((c) =>
        c._key === key ? { ...c, id_metadatos: newValue ? newValue.id : null } : c
      )
    );
  };

  const agregarCampo = () =>
    setCampos((prev) => [...prev, { ...campoVacio(), orden: prev.length + 1 }]);

  const eliminarCampo = (key) =>
    setCampos((prev) => prev.filter((c) => c._key !== key));

  const handleSubmit = async () => {
    if (!form.nombre.trim()) {
      showSnack("El nombre es obligatorio");
      return;
    }
    if (campos.some((c) => !c.etiqueta.trim() || !c.clave_textract.trim())) {
      showSnack("Todos los campos deben tener etiqueta y clave Textract");
      return;
    }

    let idPlantilla = plantilla?.id;

    const formPayload = { ...form, id_tipo_contenidos: form.id_tipo_contenidos || null };

    if (esEdicion) {
      const res = await ocrPlantillasActualizar({ id: idPlantilla, ...formPayload });
      const ok = res?.status === "true";
      if (!ok) {
        showSnack(res?.message ?? "Error al actualizar plantilla");
        return;
      }
    } else {
      const res = await ocrPlantillasCrear(formPayload);
      const ok = res?.status === "true";
      if (!ok) {
        showSnack(res?.message ?? "Error al crear plantilla");
        return;
      }
      try {
        idPlantilla = JSON.parse(atob(res.wsResponse))?.id;
      } catch { /* idPlantilla queda undefined */ }
    }

    // Guardar campos
    const camposPayload = campos.map(({ _key, ...rest }, idx) => ({
      ...rest,
      orden: idx + 1,
    }));
    const resCampos = await ocrPlantillasGuardarCampos(idPlantilla, camposPayload);
    const okCampos = resCampos?.status === "true";
    if (!okCampos) {
      showSnack(resCampos?.message ?? "Error al guardar campos");
      return;
    }

    if (regiones.length > 0 || imagenReferencia) {
      try {
        await ocrPlantillasGuardarRegiones(idPlantilla, regiones, imagenReferencia);
      } catch {
        // no bloquea el guardado principal
      }
    }

    onSuccess();
    onClose();
  };

  const metadatosParaRegiones = campos
    .filter((c) => c.id_metadatos)
    .map((c) => ({ id: c.id_metadatos, label_form: c.etiqueta }));

  const body = (
    <Box sx={{ minWidth: { xs: "100%", sm: 560 } }}>
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: "divider", px: 2.5, pt: 1 }}>
        <Tab label="Configuración" />
        <Tab label={`Regiones${regiones.length > 0 ? ` (${regiones.length})` : ""}`} />
      </Tabs>

      {/* Tab 0: Configuración */}
      {activeTab === 0 && (
      <Box sx={{ p: 2.5 }}>
      {/* ── Datos de la plantilla ── */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Nombre"
            fullWidth
            size="small"
            value={form.nombre}
            onChange={handleFormChange("nombre")}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripción"
            fullWidth
            size="small"
            multiline
            rows={2}
            value={form.descripcion}
            onChange={handleFormChange("descripcion")}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={form.tipo}
              onChange={handleFormChange("tipo")}
            >
              {TIPO_PLANTILLA_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Umbral de confianza (%)"
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0, max: 100, step: 1 }}
            value={form.umbral_confianza}
            onChange={handleFormChange("umbral_confianza")}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            size="small"
            options={tiposContenidosList}
            getOptionLabel={(o) => `${o.id} - ${o.detalle}`}
            value={tiposContenidosList.find((tc) => String(tc.id) === String(form.id_tipo_contenidos)) ?? null}
            onChange={(_, newValue) =>
              setForm((prev) => ({ ...prev, id_tipo_contenidos: newValue ? newValue.id : null }))
            }
            isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
            renderInput={(params) => (
              <TextField {...params} label="Tipo de contenido vinculado" size="small" />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            size="small"
            options={cuentasOpciones}
            getOptionLabel={(o) => `${o.id_cuentas} - ${o.razon_social ?? ""}`}
            value={cuentasOpciones.filter(c => form.cuentas_ids.includes(Number(c.id_cuentas)))}
            onChange={(_, newValue) =>
              setForm(prev => ({ ...prev, cuentas_ids: newValue.map(c => Number(c.id_cuentas)) }))
            }
            isOptionEqualToValue={(o, v) => String(o.id_cuentas) === String(v.id_cuentas)}
            renderInput={(params) => (
              <TextField {...params} label="Clientes vinculados (opcional)" size="small" />
            )}
          />
        </Grid>
      </Grid>

      {/* ── Campos de extracción ── */}
      <Box sx={{ mt: 3, mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Campos de extracción
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={agregarCampo}
          variant="outlined"
        >
          Agregar campo
        </Button>
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Etiqueta</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Clave Textract</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Oblig.
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Campo de metadatos</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {campos.map((campo) => (
              <TableRow key={campo._key}>
                <TableCell sx={{ p: "4px 8px" }}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="ej: Fecha remito"
                    value={campo.etiqueta}
                    onChange={handleCampoChange(campo._key, "etiqueta")}
                    sx={{ minWidth: 130 }}
                  />
                </TableCell>
                <TableCell sx={{ p: "4px 8px" }}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="ej: FECHA REMITO"
                    value={campo.clave_textract}
                    onChange={handleCampoChange(campo._key, "clave_textract")}
                    sx={{ minWidth: 130 }}
                  />
                </TableCell>
                <TableCell sx={{ p: "4px 8px" }}>
                  <Select
                    variant="standard"
                    size="small"
                    value={campo.tipo_campo}
                    onChange={handleCampoChange(campo._key, "tipo_campo")}
                    sx={{ minWidth: 100 }}
                  >
                    {TIPO_CAMPO_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="center" sx={{ p: "4px 8px" }}>
                  <Checkbox
                    size="small"
                    checked={Boolean(campo.obligatorio)}
                    onChange={handleCampoChange(campo._key, "obligatorio")}
                  />
                </TableCell>
                <TableCell sx={{ p: "4px 8px", minWidth: 200 }}>
                  <Autocomplete
                    size="small"
                    options={metadatosDisponibles(campo._key)}
                    getOptionLabel={(o) => o.label_form ?? ""}
                    value={
                      campo.id_metadatos
                        ? metadatosOpciones.find((m) => String(m.id) === String(campo.id_metadatos)) ?? null
                        : null
                    }
                    onChange={(_, newValue) => handleCampoMetadatos(campo._key, newValue)}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    noOptionsText={
                      !form.id_tipo_contenidos
                        ? "Seleccioná un tipo de contenido primero"
                        : "Sin metadatos disponibles"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        placeholder={form.id_tipo_contenidos ? "Sin asignar" : "— elegir tipo primero —"}
                        size="small"
                      />
                    )}
                  />
                </TableCell>
                <TableCell sx={{ p: "4px 4px" }}>
                  <Tooltip title="Eliminar campo">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => eliminarCampo(campo._key)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      </Box>
      )}

      {/* Tab 1: Regiones */}
      {activeTab === 1 && (
        <Box sx={{ p: 1.5, minHeight: 480 }}>
          {metadatosParaRegiones.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Asigna campos de metadatos en la pestaña "Configuración" para poder definir regiones.
            </Alert>
          ) : (
            <RegionesEditor
              metadatos={metadatosParaRegiones}
              regiones={regiones}
              onChange={setRegiones}
              imagenReferencia={imagenReferencia}
              onImagenChange={setImagenReferencia}
            />
          )}
        </Box>
      )}

      {/* ── Acciones (siempre visibles) ── */}
      <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, display: "flex", justifyContent: "flex-end", gap: 1, borderTop: 1, borderColor: "divider" }}>
        <Button variant="outlined" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          {esEdicion ? "Guardar cambios" : "Crear plantilla"}
        </Button>
      </Box>

      <SnackbarMessenger
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        close={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );

  return (
    <SimpleDialogComponent
      open={open}
      modalName={esEdicion ? "Editar plantilla" : "Nueva plantilla"}
      child={body}
      handleClose={onClose}
      buttonEnviar={false}
      buttonCancelar={false}
      maxWidth="lg"
    />
  );
};
